import React, { useEffect, useState } from 'react'
import {
	Box,
	CircularProgress,
	Paper,
	Typography,
} from '@mui/material'
import { AppCardTitle, AppNote, AppResultsSkeleton } from '@components'
import { displayDay } from '@utils'
import { BarChart }  from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'

import { MbDelta, mbLegendBottom } from 'Pages/MyBusiness/_shared/Ui'
import { buildTickFormatter, pickTickNumber } from './dateFormat'
import { dsColorNew } from '@palette'

/**
 * Booking Pace widget — bookings entered per day vs same-day PY.
 *
 * Honours the dashboard's date range so it stays in sync with the
 * other widgets on the page. The chart compares each day in the
 * window to the same calendar day one year earlier; the chip in the
 * header is the window-total delta.
 *
 *   <BookingPaceCard from="2026-01-01" to="2026-06-11"
 *                    offices={['MAD']} categories={['ECMR']}/>
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)

export default function BookingPaceCard({ from, to, offices = [], categories = [], duration = 'total' }) {
	const [data,    setData]    = useState(null)
	const [loading, setLoading] = useState(true)
	const [error,   setError]   = useState(null)

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		if (duration && duration !== 'total') qs.set('duration', duration)
		fetch(`/api/my-business/booking-pace?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(','), duration])

	if (error) return (
		<Paper sx={{ p: 2 }}>
			<AppNote severity="error">Booking Pace unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton chart height={300}/>

	const dates  = data.data.map(p => p.date)
	const cur    = data.data.map(p => p.bookings)
	const py     = data.data.map(p => p.bookings_py)
	const gap    = data.totals.gap_pct

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="baseline" justifyContent="space-between"
			     flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
				<AppCardTitle icon="gauge-high" title="Booking pace"
				              subtitle={`${displayDay(data.from)} → ${displayDay(data.to)} · ${data.window_days} days`}/>
			</Box>

			<Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
				<Typography sx={{ fontSize: 14, color: dsColorNew.textDark }}>
					<strong>{fmtNum(data.totals.bookings)}</strong> bookings entered
				</Typography>
				<Typography sx={{ fontSize: 14, color: dsColorNew.textMuted }}>
					vs <strong>{fmtNum(data.totals.bookings_py)}</strong> same days PY
				</Typography>
				{gap != null && <MbDelta value={gap}/>}
			</Box>

			{/* <=30 days → side-by-side bars (easy to compare daily); >30
			    → lines (otherwise the bars compress into a barcode). */}
			{dates.length <= 30 ? (
				<BarChart
					xAxis={[{ data: dates, scaleType: 'band',
					          valueFormatter: buildTickFormatter(dates),
					          tickNumber: pickTickNumber(dates) }]}
					series={[
						{ data: cur, label: 'Bookings entered', color: dsColorNew.webBlue500,
						  valueFormatter: fmtNum },
						{ data: py,  label: 'Same days PY',     color: dsColorNew.textSubtle,
						  valueFormatter: fmtNum },
					]}
					height={260}
					slotProps={mbLegendBottom}
					margin={{ top: 16, right: 16, bottom: 72, left: 56 }}/>
			) : (
				<LineChart
					xAxis={[{ data: dates, scaleType: 'point',
					          valueFormatter: buildTickFormatter(dates),
					          tickNumber: pickTickNumber(dates) }]}
					series={[
						{ data: cur, label: 'Bookings entered', color: dsColorNew.webBlue500,
						  showMark: false, curve: 'monotoneX', area: true,
						  valueFormatter: fmtNum },
						{ data: py,  label: 'Same days PY',     color: dsColorNew.textSubtle,
						  showMark: false, curve: 'monotoneX',
						  valueFormatter: fmtNum },
					]}
					height={260}
					slotProps={mbLegendBottom}
					margin={{ top: 16, right: 16, bottom: 72, left: 56 }}
					sx={{ '& .MuiAreaElement-root': { fillOpacity: 0.14 } }}/>
			)}

			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1 }}>
				Counts bookings by <strong>creation date</strong> (when entered, not
				when picked up). The gap chip compares the window total vs the same
				calendar days one year ago — apples-to-apples booking rate health.
			</Typography>
		</Paper>
	)
}
